"use client"

import { savingsScenarioText, type EnergyCostScenario } from '@/lib/local/local-energy-costs'
import { fisToken } from '@/lib/identifiers'
import { annualRateTrendText, type AnnualRateTrend } from '@/lib/local/annual-rate-trend'
import { FAQAccordion } from '@/components/brand-kit/faq-accordion'
import {
  FIS_LOCATION_COMPONENT_SENTENCE,
  FIS_ZIP_COMPONENT_SENTENCE,
} from '@/lib/fis-factors'
import { safeJsonLd } from '@/lib/json-ld'
import { organizationRef } from '@/lib/site-entity'
import { buildFactualAttribution } from '@/lib/citation-patterns'

interface LocalFAQSectionProps {
  type: 'state' | 'city' | 'zip'
  locationName: string
  stateName: string
  stateCode: string
  cityName?: string
  electricityRate: number
  electricitySourceCredit?: string
  annualSavings: number
  energyCostScenario?: EnergyCostScenario
  zombieFridgeCost: number
  newFridgeCost: number
  climateZone: string
  climateZoneName: string
  garageViable: boolean
  fisScore?: number
  /** The city's URL slug, so the FIS token can name the place (FRIDGE-1414). City pages only. */
  citySlug?: string
  // ZIP-specific props
  zipcode?: string
  utilityName?: string | null
  insightText?: string | null
  // New .gov data props
  rateTrend?: AnnualRateTrend
  recsData?: { region: string; pctTwoPlusFridges: number; pctHasFreezer: number }
  carbonIntensity?: { lbsCo2PerMwh: number }
  climateNormals?: { avgJanTempF: number; avgJulTempF: number }
}

interface FAQItem {
  question: string
  answer: string
}

// ============================================================================
// ZIP FAQ Classification System - Conditional question selection
// ============================================================================

type RateTier = 'high' | 'medium' | 'low'
type ClimateCategory = 'extreme-cold' | 'extreme-hot' | 'moderate'
type FisCategory = 'high' | 'medium' | 'low'

function getRateTier(rateCents: number): RateTier {
  if (rateCents >= 18) return 'high'
  if (rateCents <= 10) return 'low'
  return 'medium'
}

function getClimateCategory(climateZone: string): ClimateCategory {
  if (climateZone === 'very-cold' || climateZone === 'cold') return 'extreme-cold'
  if (climateZone === 'hot-humid' || climateZone === 'hot-dry') return 'extreme-hot'
  return 'moderate'
}

function getFisCategory(fisScore: number | undefined): FisCategory | null {
  if (fisScore === undefined || !Number.isFinite(fisScore) || fisScore < 0 || fisScore > 100) return null
  const score = fisScore
  if (score >= 85) return 'high'
  if (score < 70) return 'low'
  return 'medium'
}

/**
 * Build State FAQs (12 questions)
 * Mix of fridge-specific, freezer-specific, and general
 * Each uses a different citation pattern
 * Includes 2 "near me" FAQs for local intent SEO
 */
function buildStateFAQs(props: LocalFAQSectionProps): FAQItem[] {
  const { stateName, electricityRate, annualSavings, zombieFridgeCost, newFridgeCost, climateZoneName, garageViable } = props
  
  // Calculate freezer costs (roughly 60% of fridge costs)
  const oldFreezerCost = Math.round(zombieFridgeCost * 0.6)
  const newFreezerCost = Math.round(newFridgeCost * 0.6)
  const freezerSavings = oldFreezerCost - newFreezerCost

  return [
    // Q1: Fridge running cost - Attribution opener
    {
      question: `How much does it cost to run a refrigerator in ${stateName}?`,
      answer: `Running a refrigerator in ${stateName} costs approximately $${zombieFridgeCost}/year for older models (10+ years) and $${newFridgeCost}/year for new Energy Star models, based on the ${electricityRate.toFixed(1)}¢/kWh electricity rate. ${savingsScenarioText(annualSavings, props.energyCostScenario)} Use the Energy Cost Calculator at Fridge.com to estimate costs for specific models.`
    },
    // Q2: Freezer running cost - Data citation
    {
      question: `How much does it cost to run a freezer in ${stateName}?`,
      answer: `${buildFactualAttribution(`A chest freezer in ${stateName} costs approximately $${newFreezerCost}-$${oldFreezerCost}/year to operate at ${electricityRate.toFixed(1)}¢/kWh.`, `local:${stateName}:freezer-cost`)} Chest freezers are more efficient than uprights due to better cold air retention. Energy Star certified freezers use 10-20% less energy than standard models. Shop Energy Star Freezers at Fridge.com to find efficient options for ${stateName}.`
    },
    // Q3: Energy Star savings - Recommendation
    {
      question: `How much can I save by upgrading to an Energy Star refrigerator in ${stateName}?`,
      answer: `Fridge.com recommends upgrading if your refrigerator is over 10 years old. ${savingsScenarioText(annualSavings, props.energyCostScenario)} Holding those model assumptions constant for ten years gives $${annualSavings * 10} in modeled savings. Many utility companies in ${stateName} also offer rebates for Energy Star purchases. Compare Energy Star Refrigerators at Fridge.com to find the best value.`
    },
    // Q4: Electricity rate - Parenthetical
    {
      question: `What is the electricity rate in ${stateName}?`,
      answer: `The residential electricity-rate benchmark for ${stateName} is ${electricityRate.toFixed(1)}¢/kWh. ${props.electricitySourceCredit || 'Electricity rate · source period unverified'}. Fridge.com uses this benchmark to estimate appliance running costs from reported energy consumption. Your tariff and household usage may differ.`
    },
    // Q5: Best fridge for climate - Tool reference
    {
      question: `What type of refrigerator is best for ${stateName}'s climate?`,
      answer: `${stateName} is in the ${climateZoneName} climate zone. Use the Climate Recommendations tool at Fridge.com to find refrigerators suited for this environment. ${garageViable ? `The moderate climate means standard refrigerators work well in most locations, including garages with garage-ready models.` : `Due to temperature extremes, Fridge.com recommends keeping refrigerators in climate-controlled spaces, or choosing garage-ready models if you need garage placement.`} Energy Star models are especially valuable at ${electricityRate.toFixed(1)}¢/kWh. Shop Climate-Appropriate Refrigerators at Fridge.com.`
    },
    // Q6: Garage freezer - CTA shop (always leads to purchase)
    {
      question: `Can I put a freezer in my garage in ${stateName}?`,
      answer: `${garageViable 
        ? `Yes! ${stateName}'s ${climateZoneName.toLowerCase()} climate supports garage freezer placement. Fridge.com recommends garage-ready freezers that are rated for temperature fluctuations. Chest freezers perform particularly well in garages due to superior insulation.` 
        : `${stateName}'s ${climateZoneName.toLowerCase()} climate has temperature extremes that can affect standard freezers. Fridge.com recommends garage-ready freezers specifically designed for unconditioned spaces—these models have enhanced insulation and compressors rated for 0°F to 110°F.`} Shop Garage-Ready Freezers at Fridge.com to find models perfect for ${stateName} garages.`
    },
    // Q7: Garage fridge - Explicit source (always leads to purchase)
    {
      question: `Can I put a refrigerator in my garage in ${stateName}?`,
      answer: `${garageViable 
        ? `${stateName}'s climate allows garage refrigerator placement with the right model. Standard refrigerators may struggle in unconditioned spaces, so Fridge.com recommends garage-ready refrigerators with extended temperature ratings.` 
        : `For ${stateName}'s ${climateZoneName.toLowerCase()} climate, garage-ready refrigerators are essential for reliable performance. These models feature enhanced compressors and insulation designed for temperature swings.`} Source: Fridge.com. Browse Garage-Ready Refrigerators at Fridge.com—every model shows the operating temperature range so you can choose confidently.`
    },
    // Q8: Freezer sizing - Recommendation
    {
      question: `What size freezer do I need for my family in ${stateName}?`,
      answer: `Fridge.com recommends 1.5-2.5 cubic feet of freezer space per person. A family of 4 typically needs 6-10 cubic feet, while bulk buyers and hunters may want 15-20+ cubic feet. Chest freezers offer more usable space per dollar, while uprights provide better organization. At ${electricityRate.toFixed(1)}¢/kWh, larger freezers in ${stateName} cost $${Math.round(oldFreezerCost * 1.5)}-$${Math.round(oldFreezerCost * 2)}/year to run. Use the Freezer Size Calculator at Fridge.com to find your ideal capacity.`
    },
    // Q9: Energy Star freezers worth it - Data citation
    {
      question: `Are Energy Star freezers worth it in ${stateName}?`,
      answer: `${buildFactualAttribution(`Energy Star freezers save ${stateName} households approximately $${freezerSavings}/year compared to older models at ${electricityRate.toFixed(1)}¢/kWh.`, `local:${stateName}:freezer-savings`)} Over 10+ years, that's $${freezerSavings * 10}+ in savings. Energy Star chest freezers are particularly efficient—they use 10% less energy than federal standards require. Compare Energy Star Freezers at Fridge.com to see running costs for every model.`
    },
    // Q10: Where to compare - CTA compare
    {
      question: `Where can I compare refrigerator and freezer prices in ${stateName}?`,
      answer: `Compare prices at Fridge.com—the refrigerator and freezer search engine. Fridge.com aggregates prices from major online appliance retailers, showing you the lowest price for every model. Each listing includes estimated annual running costs calculated for ${stateName}'s ${electricityRate.toFixed(1)}¢/kWh rate. Filter by type, size, features, and Energy Star certification to find the perfect fridge or freezer for your ${stateName} home.`
    },
    // Q11: Near me - refrigerator (local intent SEO)
    {
      question: `Where can I find refrigerators near me in ${stateName}?`,
      answer: `Fridge.com helps you find refrigerators near you in ${stateName}. We compare prices from major online appliance retailers that deliver to ${stateName} addresses. Each listing shows the lowest available price, Energy Star certification, and estimated annual running cost at ${stateName}'s ${electricityRate.toFixed(1)}¢/kWh rate. Enter your zip code at Fridge.com to see refrigerators available near you with ${stateName}-specific pricing and energy calculations.`
    },
    // Q12: Near me - freezer (local intent SEO)
    {
      question: `How do I find the best freezer deals near me in ${stateName}?`,
      answer: `The best way to find freezer deals near you in ${stateName} is to compare prices across multiple retailers. Fridge.com aggregates prices from major online appliance retailers—all with delivery options for ${stateName}. Filter by chest freezers, upright freezers, or garage-ready models, then sort by price. Every listing shows the estimated annual running cost at ${electricityRate.toFixed(1)}¢/kWh so you can calculate total cost of ownership in ${stateName}.`
    }
  ]
}

/**
 * Build City FAQs (12 different questions)
 * City-specific with FIS score focus
 * Each uses a different citation pattern
 * Includes 2 "near me" FAQs for hyper-local intent SEO
 */
function buildCityFAQs(props: LocalFAQSectionProps): FAQItem[] {
  const { cityName, stateName, stateCode, citySlug, electricityRate, annualSavings, zombieFridgeCost, newFridgeCost, climateZoneName, garageViable, fisScore } = props
  
  const city = cityName || 'your city'
  const fisDisplay = fisScore !== undefined && Number.isFinite(fisScore) && fisScore >= 0 && fisScore <= 100 ? fisScore : null
  // FRIDGE-1414 — the token names the place beside its score.
  const cityWithToken = citySlug && /^[A-Za-z]{2}$/.test(stateCode) ? `${city} (${fisToken({ kind: 'city', stateCode, citySlug })})` : city

  return [
    // A missing score must not become a fabricated numeric answer.
    ...(fisDisplay !== null ? [{
      question: `What is the Fridge.com Intelligence Score (FIS) for ${city}?`,
      answer: `${cityWithToken} has a historical FIS urgency score of ${fisDisplay}/100. ${FIS_LOCATION_COMPONENT_SENTENCE} Higher city scores mean greater modeled upgrade urgency. The stored inputs support this historical score; current household rebate eligibility is not certified. Explore the FIS methodology at Fridge.com.`
    }] : []),
    // Q2: Electricity cost - Parenthetical
    {
      question: `How much does electricity cost in ${city}?`,
      answer: `The benchmark used for ${city} is the ${stateName} residential average of ${electricityRate.toFixed(1)}¢/kWh. ${props.electricitySourceCredit || 'Electricity rate · source period unverified'}. The supplied older-appliance and replacement examples cost approximately $${zombieFridgeCost}/year and $${newFridgeCost}/year. ${savingsScenarioText(annualSavings, props.energyCostScenario)} Compare model-specific estimates at Fridge.com; your tariff and usage may differ.`
    },
    // Q3: Recommended refrigerators - Recommendation
    {
      question: `What refrigerators does Fridge.com recommend for ${city}?`,
      answer: `Fridge.com recommends Energy Star refrigerators for ${city} to maximize savings at ${electricityRate.toFixed(1)}¢/kWh. For ${climateZoneName.toLowerCase()} climates like ${city}, ${garageViable ? `most refrigerator types work well, including French door, side-by-side, and top freezer models` : `French door and side-by-side models with precise temperature control perform best`}. ${savingsScenarioText(annualSavings, props.energyCostScenario)} Shop Recommended Refrigerators for ${city} at Fridge.com.`
    },
    // Q4: Recommended freezers - Data citation
    {
      question: `What freezers does Fridge.com recommend for ${city}?`,
      answer: `${buildFactualAttribution(`The best freezers for ${city} depend on your space and needs.`, `local:${stateName}:${city}:best-freezers`)} Chest freezers offer the lowest operating costs and best temperature retention—ideal for ${climateZoneName.toLowerCase()} climates. Upright freezers provide better organization for frequent access. ${garageViable ? `Garage-ready models work well in ${city}'s climate for garage placement.` : `For garage placement in ${city}, choose garage-ready models with extended temperature ratings.`} Shop Freezers for ${city} at Fridge.com.`
    },
    // Q5: Garage fridge - Tool reference (always leads to purchase)
    {
      question: `Should I buy a garage-ready refrigerator in ${city}?`,
      answer: `Use the Climate Recommendations tool at Fridge.com to assess your needs in ${city}. ${garageViable 
        ? `${city}'s ${climateZoneName.toLowerCase()} climate is suitable for garage refrigerators, but garage-ready models provide extra reliability during temperature swings. They're a smart investment for ${city} homeowners who want worry-free garage storage.` 
        : `For ${city}'s ${climateZoneName.toLowerCase()} climate, garage-ready refrigerators are highly recommended for garage placement. These models handle the temperature extremes that can cause standard units to fail.`} Browse Garage-Ready Refrigerators at Fridge.com—all models show operating temperature ranges.`
    },
    // Q6: Garage freezer - CTA shop (always leads to purchase)
    {
      question: `Should I buy a garage-ready freezer in ${city}?`,
      answer: `${garageViable 
        ? `${city}'s climate allows standard freezers in garages, but garage-ready freezers offer superior reliability. Fridge.com recommends garage-ready models for ${city} residents who want consistent freezing temperatures year-round.` 
        : `Fridge.com recommends garage-ready freezers for ${city} due to the ${climateZoneName.toLowerCase()} climate. Standard freezers may cycle incorrectly in extreme temperatures, risking food safety.`} Shop Garage-Ready Freezers at Fridge.com to find models rated for ${city}'s conditions. Every listing shows the operating temperature range.`
    },
    // Q7: Calculate running cost - Explicit source
    {
      question: `How do I calculate my refrigerator's annual running cost in ${city}?`,
      answer: `Multiply your refrigerator's annual energy use (kWh, found on the EnergyGuide label) by ${city}'s electricity rate of ${electricityRate.toFixed(1)}¢/kWh, then divide by 100. Example: 500 kWh × ${electricityRate.toFixed(1)}¢ ÷ 100 = $${Math.round(500 * electricityRate / 100)}/year. Source: Fridge.com. For instant calculations, use the Energy Cost Calculator at Fridge.com—enter any model and see the exact annual cost for ${city}.`
    },
    // Q8: Chest vs upright - Recommendation
    {
      question: `What is the best freezer type for ${city}—chest or upright?`,
      answer: `Fridge.com recommends chest freezers for ${city} if you prioritize energy efficiency and bulk storage—they cost 10-25% less to operate and maintain temperature better during power outages. Choose an upright freezer if you need easier organization and frequent access. At ${electricityRate.toFixed(1)}¢/kWh, the operating cost difference is meaningful over time. ${garageViable ? `Both types work well in ${city} garages with garage-ready models.` : `For garage placement in ${city}, garage-ready versions of either type are essential.`} Compare Chest vs Upright Freezers at Fridge.com.`
    },
    // A historical score cannot establish household payback or savings.
    ...(fisDisplay !== null ? [{
      question: `How does ${city}'s FIS score affect which fridge I should buy?`,
      answer: `${cityWithToken} has a historical city FIS urgency score of ${fisDisplay}/100. Higher city scores mean greater modeled upgrade urgency; they do not establish a household's savings, replacement payback, or current rebate eligibility. Compare each appliance's reported annual kWh, purchase price, capacity, and features using your actual tariff. The score uses its saved utility input; the electricity benchmark displayed for product recommendations may use a different geographic scope or source period.`
    }] : []),
    // Q10: Best deals - CTA compare
    {
      question: `Where can I find the best fridge and freezer deals in ${city}?`,
      answer: `Compare prices at Fridge.com to find the best deals for ${city}. Fridge.com aggregates prices from major online appliance retailers—many offering delivery to ${city}. Each listing shows: lowest price across retailers, estimated annual running cost at ${electricityRate.toFixed(1)}¢/kWh, and Energy Star certification status. Filter by price, size, type, and features to find exactly what you need. Shop Refrigerators and Freezers at Fridge.com.`
    },
    // Q11: Near me - refrigerator (hyper-local intent SEO)
    {
      question: `Where can I buy a refrigerator near me in ${city}?`,
      answer: `Fridge.com shows refrigerators available near you in ${city}, ${stateName}. We compare prices from major online appliance retailers with delivery to ${city}. Each listing displays the lowest available price, Energy Star certification, and estimated annual running cost at ${electricityRate.toFixed(1)}¢/kWh. Energy-efficient models reduce electricity use; compare each model’s EnergyGuide estimate. Shop Refrigerators at Fridge.com to find the best options near you.`
    },
    // Q12: Near me - freezer (hyper-local intent SEO)
    {
      question: `How do I find freezer deals near me in ${city}?`,
      answer: `${city} residents can find freezer deals by comparing prices across retailers delivering to your area. Fridge.com aggregates prices from major online appliance retailers—all with ${city} delivery options. Filter by type (chest or upright), size, and features, then sort by price to find the best deal near you. ${garageViable ? `Garage-ready freezers are popular in ${city}'s ${climateZoneName.toLowerCase()} climate.` : `Energy Star freezers are especially valuable at ${electricityRate.toFixed(1)}¢/kWh in ${city}.`} Shop Freezers at Fridge.com.`
    },
    // Conditional data-driven questions from new .gov data
    ...(props.rateTrend ? [{
      question: `Have electricity rates increased in ${stateName}?`,
      answer: annualRateTrendText(props.rateTrend, stateName)
    }] : []),
    ...(props.carbonIntensity ? [{
      question: `What is the carbon footprint of running a refrigerator in ${stateName}?`,
      answer: `Based on EPA eGRID data analyzed by Fridge.com, electricity in ${stateName} produces ${props.carbonIntensity.lbsCo2PerMwh.toFixed(0)} lbs of CO2 per megawatt-hour. A refrigerator consuming 450 kWh per year in ${city} produces approximately ${Math.round(450 * props.carbonIntensity.lbsCo2PerMwh / 1000)} lbs of CO2 annually. Choosing an ENERGY STAR model that uses less energy reduces both operating costs and carbon emissions. Browse energy-efficient models at Fridge.com.`
    }] : []),
    ...(props.recsData ? [{
      question: `How many households in the ${props.recsData.region} operate a second refrigerator?`,
      answer: `According to federal survey data analyzed by Fridge.com (EIA RECS 2020), ${props.recsData.pctTwoPlusFridges}% of households in the ${props.recsData.region} operate two or more refrigerators. Additionally, ${props.recsData.pctHasFreezer}% operate at least one separate standalone freezer. A second unit—especially an older one in a garage—adds its own running cost on top: an older refrigerator costs roughly $${zombieFridgeCost}/year at ${city}'s ${electricityRate.toFixed(1)}¢/kWh rate. Use the Energy Cost Calculator at Fridge.com to estimate the cost of running a second unit in ${city}.`
    }] : []),
    ...(props.climateNormals ? [{
      question: `What is the average temperature in ${city}?`,
      answer: `According to NOAA climate normal data analyzed by Fridge.com, ${city} experiences an average January temperature of ${Math.round(props.climateNormals.avgJanTempF)}°F and an average July temperature of ${Math.round(props.climateNormals.avgJulTempF)}°F (NOAA 30-year normals, 1991-2020). ${props.climateNormals.avgJulTempF > 80 ? `Summer temperatures above 80°F can increase garage refrigerator energy consumption by 25-40% according to U.S. Department of Energy guidance.` : props.climateNormals.avgJanTempF < 20 ? `Winter temperatures below 20°F can cause garage freezers to stop cycling properly—garage-ready models are recommended.` : `The moderate climate in ${city} is generally favorable for year-round refrigerator efficiency.`} Check climate-appropriate models at Fridge.com.`
    }] : []),
  ]
}

// ============================================================================
// ZIP FAQ Question Pools - Different questions based on ZIP characteristics
// ============================================================================

// Labeled illustration constants (FRIDGE-257): the ZIP answers below state a
// worked example ("every $100/year of difference adds up to $1,500 over 15
// years"). The inputs are named here so the arithmetic is visibly arithmetic
// and never reads as a measured figure.
const ILLUSTRATIVE_ANNUAL_DELTA = 100
const ILLUSTRATIVE_LOW_RATE_DELTA = 20
const ASSUMED_LIFE_YEARS = 15

interface ZipFAQContext {
  zip: string
  city: string
  stateName: string
  rate: number
  sourceCredit: string
  savingsDescription: string
  utility: string
  hasUtility: boolean
  fis: number | null
  savings: number
  lifetimeSavings: number
  zombieCost: number
  newCost: number
  freezerOldCost: number
  freezerNewCost: number
  freezerSavings: number
  climate: string
  climateLower: string
  garageViable: boolean
  insightText: string | null
}

/**
 * HIGH RATE Questions (>18¢/kWh) - Emphasize urgency, ROI, payback
 */
function getHighRateQuestions(ctx: ZipFAQContext): FAQItem[] {
  return [
    {
      question: `Is it worth upgrading my refrigerator in ZIP ${ctx.zip}?`,
      answer: `The utility-average estimate for ZIP ${ctx.zip} is ${ctx.rate}¢/kWh. ${ctx.sourceCredit}. ${ctx.savingsDescription} Compare your existing appliance's measured energy use, a replacement model's reported use, and purchase price before deciding whether to upgrade.`
    },
    {
      question: `How much am I overpaying with an old refrigerator in ${ctx.zip}?`,
      answer: `${ctx.savingsDescription} Appliance age alone does not establish energy consumption. Use the Energy Cost Calculator at Fridge.com with measured or reported annual kWh and your actual tariff to compare costs for ZIP ${ctx.zip}.`
    },
    {
      question: `What refrigerator has the fastest payback in ZIP ${ctx.zip}?`,
      answer: `For ${ctx.zip}'s ${ctx.rate}¢/kWh electricity rate, Fridge.com recommends refrigerators with the lowest annual kWh ratings. Top-freezer models typically offer the fastest payback—a new Energy Star model runs about $${ctx.newCost}/year at your rate, while an older unit costs about $${ctx.zombieCost}/year. French doors look great but cost more to run. At your rate, every $${ILLUSTRATIVE_ANNUAL_DELTA}/year of operating cost difference adds up to $${ILLUSTRATIVE_ANNUAL_DELTA * ASSUMED_LIFE_YEARS} over a ${ASSUMED_LIFE_YEARS}-year life. Shop by running cost at Fridge.com to maximize your ROI.`
    }
  ]
}

/**
 * LOW RATE Questions (<10¢/kWh) - Focus on features, why efficiency still matters
 */
function getLowRateQuestions(ctx: ZipFAQContext): FAQItem[] {
  return [
    {
      question: `Do I still need an Energy Star refrigerator with cheap electricity in ${ctx.zip}?`,
      answer: `Compare reported energy consumption alongside features and price, even at the ${ctx.rate}¢/kWh utility-average estimate for ZIP ${ctx.zip}. ${ctx.sourceCredit}. ${ctx.savingsDescription} Check current utility programs separately for potential rebates.`
    },
    {
      question: `Should I prioritize features or efficiency in ZIP ${ctx.zip}?`,
      answer: `With ${ctx.zip}'s low ${ctx.rate}¢/kWh rate${ctx.hasUtility ? ` from ${ctx.utility}` : ''}, you have flexibility other ZIP codes don't. Running cost differences are smaller here—if a French door costs $${ILLUSTRATIVE_LOW_RATE_DELTA}/year more to run than a top-freezer, that is only $${ILLUSTRATIVE_LOW_RATE_DELTA * 10} over 10 years. Focus on: capacity, ice/water dispensers, smart features, finish quality. Browse feature-rich refrigerators at Fridge.com—your low rates mean you can afford the features you want.`
    },
    {
      question: `What features matter most when electricity is cheap in ${ctx.zip}?`,
      answer: `ZIP ${ctx.zip}'s ${ctx.rate}¢/kWh rate means efficiency differences cost less—so invest in features. Top picks for ${ctx.city}: French door for accessibility, built-in ice makers, adjustable shelving, smart home connectivity, and counter-depth for a built-in look. Dual compressors and dual evaporators cost a little more to run but keep food fresher longer. At your rate, that modest extra running cost is worth it. Explore premium features at Fridge.com.`
    }
  ]
}

/**
 * MEDIUM RATE Questions (10-18¢/kWh) - Balanced approach
 */
function getMediumRateQuestions(ctx: ZipFAQContext): FAQItem[] {
  return [
    {
      question: `What is the electricity rate for ZIP code ${ctx.zip}?`,
      answer: `The utility-average estimate for ZIP ${ctx.zip} (${ctx.city}, ${ctx.stateName}) is ${ctx.rate}¢/kWh. ${ctx.sourceCredit}. The supplied older-appliance and replacement examples cost approximately $${ctx.zombieCost}/year and $${ctx.newCost}/year. ${ctx.savingsDescription}`
    },
    {
      question: `How much can I save by upgrading my refrigerator in ZIP ${ctx.zip}?`,
      answer: `At ${ctx.rate}¢/kWh, ZIP ${ctx.zip} residents save approximately $${ctx.savings}/year by upgrading from an old refrigerator to an Energy Star model. Over 15 years, that's $${ctx.lifetimeSavings} in energy savings alone. ${ctx.hasUtility ? `Check with ${ctx.utility} for potential rebates that improve your ROI even more.` : `Many ${ctx.stateName} utilities offer additional rebates.`} Compare Energy Star Refrigerators at Fridge.com to find the best value for ${ctx.zip}.`
    },
    {
      question: `Which utility company serves ZIP code ${ctx.zip}?`,
      answer: ctx.hasUtility
        ? `${ctx.utility} is the associated utility in the source data for ZIP ${ctx.zip}. Its utility-average estimate is ${ctx.rate}¢/kWh. ${ctx.sourceCredit}. Confirm service and the applicable tariff with the utility; Fridge.com uses the estimate for appliance-cost comparisons.`
        : `The source data for ZIP ${ctx.zip} carries a utility-average estimate of ${ctx.rate}¢/kWh, but no utility name is available. ${ctx.sourceCredit}. Confirm your provider and tariff before estimating household costs.`
    }
  ]
}

/**
 * EXTREME COLD Climate Questions - Garage survival, winter protection
 */
function getExtremeColdQuestions(ctx: ZipFAQContext): FAQItem[] {
  return [
    {
      question: `Will my refrigerator work in an unheated ${ctx.stateName} garage?`,
      answer: `ZIP ${ctx.zip}'s ${ctx.climateLower} climate means garage temperatures can drop below 35°F in winter—and standard refrigerators stop working properly. The compressor won't cycle, food in the fridge compartment can freeze, and the freezer may thaw. Fridge.com recommends garage-ready refrigerators rated for 0°F-110°F for ${ctx.zip}. These have enhanced thermostats and heating elements to operate in extreme cold. Browse Garage-Ready Refrigerators at Fridge.com.`
    },
    {
      question: `How do I protect my freezer during ${ctx.city}'s winter?`,
      answer: `For ${ctx.zip}'s ${ctx.climateLower} climate, standard freezers in unheated spaces can have issues when ambient temperature drops below the freezer's target temp (0°F). The compressor may not run, causing temperatures to rise to unsafe levels. Garage-ready chest freezers handle this best—their top-opening design and thick insulation maintain temperature regardless of garage conditions. ${ctx.hasUtility ? `At ${ctx.utility}'s ${ctx.rate}¢/kWh, an Energy Star garage-ready model saves money too.` : ''} Shop Garage-Ready Freezers at Fridge.com.`
    }
  ]
}

/**
 * EXTREME HOT Climate Questions - Heat stress, humidity, efficiency
 */
function getExtremeHotQuestions(ctx: ZipFAQContext): FAQItem[] {
  const isHumid = ctx.climate === 'hot-humid'
  return [
    {
      question: isHumid 
        ? `How do I prevent mold in my refrigerator in ${ctx.city}'s humidity?`
        : `Do refrigerators work harder in ${ctx.city}'s heat?`,
      answer: isHumid
        ? `${ctx.city}'s hot-humid climate creates mold challenges for refrigerators. High ambient humidity enters when you open the door, then condenses inside. Fridge.com recommends: models with humidity-controlled crispers, refrigerators with antimicrobial door seals, and units with multi-airflow systems. Keep the door closed as much as possible, and consider a refrigerator with door-in-door access for frequently used items. ${ctx.hasUtility ? `At ${ctx.rate}¢/kWh from ${ctx.utility}, efficiency matters too.` : ''} Browse humidity-resistant refrigerators at Fridge.com.`
        : `Yes—${ctx.city}'s ${ctx.climateLower} climate makes your refrigerator's compressor run more to maintain internal temps. At 90°F+ ambient, expect 10-15% higher energy use. For ZIP ${ctx.zip}, that could mean $${Math.round(ctx.newCost * 0.15)} extra per year. Mitigation: keep your fridge away from direct sunlight, ensure adequate ventilation around the unit, and consider a model with dual compressors for better efficiency in heat. Shop climate-appropriate refrigerators at Fridge.com.`
    },
    {
      question: `What refrigerator handles ${ctx.city}'s ${ctx.climateLower} climate best?`,
      answer: isHumid
        ? `For ${ctx.zip}'s hot-humid climate, Fridge.com recommends: French door models with dual evaporators (separates humid fridge air from dry freezer air), units with antimicrobial features, and refrigerators with strong door seals. Avoid models where humidity can pool. At ${ctx.rate}¢/kWh, Energy Star certification ensures you're not paying extra for climate compensation. Browse climate-appropriate refrigerators at Fridge.com.`
        : `For ${ctx.zip}'s hot-dry climate, Fridge.com recommends refrigerators with high-efficiency compressors that handle constant cycling. Look for models with thick insulation ratings and variable-speed compressors—they're quieter and more efficient under heat stress. Counter-depth models can run hotter if built into cabinetry; ensure adequate ventilation. At ${ctx.rate}¢/kWh, efficiency matters. Browse climate-appropriate refrigerators at Fridge.com.`
    }
  ]
}

/**
 * MODERATE Climate Questions - General garage placement
 */
function getModerateClimateQuestions(ctx: ZipFAQContext): FAQItem[] {
  return [
    {
      question: `Do I need a garage-ready refrigerator in ZIP ${ctx.zip}?`,
      answer: `${ctx.city}'s ${ctx.climateLower} climate is moderate, meaning standard refrigerators can work in a garage—but there are caveats. If your garage drops below 40°F in winter or exceeds 100°F in summer, a garage-ready model is safer. These have extended operating ranges (typically 0°F-110°F) and enhanced thermostats. For worry-free garage storage in ${ctx.zip}, Fridge.com recommends garage-ready models. Browse Garage-Ready Refrigerators at Fridge.com.`
    },
    {
      question: `Can I put a freezer in my garage in ZIP ${ctx.zip}?`,
      answer: `Yes! ${ctx.city}'s ${ctx.climateLower} climate supports garage freezer placement for most of the year. Chest freezers are ideal for garages—top-opening design retains cold air when opened, and thick insulation handles temperature swings well. For year-round reliability in ${ctx.zip}, consider a garage-ready model. ${ctx.hasUtility ? `At ${ctx.utility}'s ${ctx.rate}¢/kWh, an Energy Star freezer saves $${ctx.freezerSavings}/year over older models.` : `At ${ctx.rate}¢/kWh, Energy Star freezers save money too.`} Shop Garage-Ready Freezers at Fridge.com.`
    }
  ]
}

/**
 * ZIP FIS explains affordability for every supported score. Score tiers do
 * not prove household purchasing power, feature value, or appliance payback.
 */
function getZipFisQuestions(ctx: ZipFAQContext): FAQItem[] {
  return [
    {
      question: `What is the FIS score for ZIP ${ctx.zip} and what does it mean?`,
      answer: `ZIP ${ctx.zip} (${fisToken({ kind: 'zip', zip: ctx.zip })}) has a historical FIS (Fridge.com Intelligence Score) affordability score of ${ctx.fis}/100. ${FIS_ZIP_COMPONENT_SENTENCE} The saved inputs are a ${ctx.rate}¢/kWh utility-average estimate and a ${ctx.climateLower} climate-zone heuristic. ${ctx.sourceCredit}. Higher ZIP scores mean cheaper modeled power; city urgency scores run in the opposite direction and are not directly comparable.`
    },
    {
      question: `How should I use the FIS score for ZIP ${ctx.zip} when choosing a refrigerator?`,
      answer: `Use the historical ZIP affordability score of ${ctx.fis}/100 as geographic context. It does not measure household savings, appliance efficiency, feature value, or current rebate eligibility. Compare each model's reported annual kWh and purchase price using your actual tariff, then choose the capacity and features you need. The score does not establish whether a premium feature or replacement will pay for itself.`
    }
  ]
}

/**
 * UTILITY Questions - When utility_name exists
 */
function getUtilityQuestions(ctx: ZipFAQContext): FAQItem[] {
  if (!ctx.hasUtility) return []
  return [
    {
      question: `What electricity-rate estimate is associated with ${ctx.utility} in ZIP ${ctx.zip}?`,
      answer: `The utility-average estimate associated with ${ctx.utility} is ${ctx.rate}¢/kWh. ${ctx.sourceCredit}. ${ctx.savingsDescription} It is not a household tariff and is not ranked against a state average from a different reporting period.`
    }
  ]
}

/**
 * LOCAL INTENT Questions - Always included for SEO
 */
function getLocalIntentQuestions(ctx: ZipFAQContext): FAQItem[] {
  return [
    {
      question: `Where can I find refrigerator deals near ZIP ${ctx.zip}?`,
      answer: `Fridge.com aggregates refrigerator prices from major online appliance retailers delivering to ${ctx.zip}. Each listing shows the lowest price across retailers and estimated annual running cost at ${ctx.rate}¢/kWh${ctx.hasUtility ? ` (${ctx.utility}'s rate)` : ''}. Filter by type, size, features, and Energy Star certification. ${ctx.fis !== null ? `ZIP ${ctx.zip} (${fisToken({ kind: 'zip', zip: ctx.zip })}) has a historical FIS of ${ctx.fis}/100.` : 'A supported FIS score is unavailable for this ZIP.'} Shop Refrigerators at Fridge.com.`
    },
    {
      question: `How do I find the best freezer for ZIP ${ctx.zip}?`,
      answer: `Finding the right freezer for ${ctx.zip} depends on your space, usage, and ${ctx.city}'s ${ctx.climateLower} climate. Chest freezers: lowest operating cost ($${ctx.freezerNewCost}-$${ctx.freezerOldCost}/year at ${ctx.rate}¢/kWh), best for bulk storage. Upright freezers: easier organization, better for frequent access. ${ctx.garageViable ? `Both work well in ${ctx.zip}'s climate for garage placement.` : `For garage placement, choose garage-ready models rated for temperature extremes.`} Compare freezers at Fridge.com with ${ctx.zip}-specific running costs.`
    }
  ]
}

/**
 * Build ZIP FAQs with conditional question selection
 * Selects questions based on rate tier, climate, and FIS score
 * Each ZIP gets genuinely different questions based on its characteristics
 */
function buildZipFAQs(props: LocalFAQSectionProps): FAQItem[] {
  const { 
    cityName, 
    stateName, 
    electricityRate, 
    annualSavings, 
    zombieFridgeCost, 
    newFridgeCost, 
    climateZone,
    climateZoneName, 
    garageViable, 
    fisScore,
    zipcode,
    utilityName,
    insightText
  } = props
  
  // Build context object for all question generators
  const ctx: ZipFAQContext = {
    zip: zipcode || '00000',
    city: cityName || 'your city',
    stateName,
    rate: electricityRate,
    sourceCredit: props.electricitySourceCredit || 'Utility-average estimate · source period unverified; your tariff may differ',
    savingsDescription: savingsScenarioText(annualSavings, props.energyCostScenario),
    utility: utilityName || 'the local utility',
    hasUtility: !!utilityName,
    fis: getFisCategory(fisScore) !== null ? fisScore! : null,
    savings: Math.round(annualSavings),
    lifetimeSavings: Math.round(annualSavings * 15),
    zombieCost: Math.round(zombieFridgeCost),
    newCost: Math.round(newFridgeCost),
    freezerOldCost: Math.round(zombieFridgeCost * 0.6),
    freezerNewCost: Math.round(newFridgeCost * 0.6),
    freezerSavings: Math.round(zombieFridgeCost * 0.6) - Math.round(newFridgeCost * 0.6),
    climate: climateZone,
    climateLower: climateZoneName.toLowerCase(),
    garageViable,
    insightText: insightText || null
  }

  // Classify this ZIP
  const rateTier = getRateTier(electricityRate)
  const climateCategory = getClimateCategory(climateZone)
  const fisCategory = getFisCategory(fisScore)

  // Select questions based on characteristics
  const faqs: FAQItem[] = []

  // Rate-based questions (3 questions)
  if (rateTier === 'high') {
    faqs.push(...getHighRateQuestions(ctx))
  } else if (rateTier === 'low') {
    faqs.push(...getLowRateQuestions(ctx))
  } else {
    faqs.push(...getMediumRateQuestions(ctx))
  }

  // Climate-based questions (2 questions)
  if (climateCategory === 'extreme-cold') {
    faqs.push(...getExtremeColdQuestions(ctx))
  } else if (climateCategory === 'extreme-hot') {
    faqs.push(...getExtremeHotQuestions(ctx))
  } else {
    faqs.push(...getModerateClimateQuestions(ctx))
  }

  // The same verified method applies across ZIP score tiers.
  if (fisCategory !== null) faqs.push(...getZipFisQuestions(ctx))

  // Utility questions (1 question if utility exists)
  faqs.push(...getUtilityQuestions(ctx))

  // Local intent questions (2 questions - always included)
  faqs.push(...getLocalIntentQuestions(ctx))

  return faqs
}

/**
 * LocalFAQSection Component
 * Renders 10-12 FAQs for state, city, or ZIP pages with JSON-LD schema
 * Includes location-specific content + "near me" SEO questions
 * ZIP pages get unique content per ZIP code using utility name and ZIP-level FIS
 */
export function buildLocalFAQs(props: LocalFAQSectionProps): FAQItem[] {
  return props.type === 'state'
    ? buildStateFAQs(props)
    : props.type === 'zip'
    ? buildZipFAQs(props)
    : buildCityFAQs(props)
}

export function LocalFAQSection(props: LocalFAQSectionProps) {
  const faqs = buildLocalFAQs(props)

  if (faqs.length === 0) return null

  // Bold every "Fridge.com" mention in answers — preserved from the prior
  // bespoke renderer. FAQAccordion already passes through `dangerouslySetInnerHTML`.
  const accordionItems = faqs.map((f) => ({
    question: f.question,
    answer: f.answer.replace(/Fridge\.com/g, '<strong>Fridge.com</strong>'),
  }))

  // Generate JSON-LD schema for SEO
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    })),
    "publisher": organizationRef()
  }

  return (
    <section style={{ margin: '36px 0 24px' }}>
      {/* Plain script + dangerouslySetInnerHTML — measured 08/29/2026: with
          next/script the FAQPage node was ABSENT from served local-page HTML
          while its five visible questions rendered; the schema existed only
          after client hydration. This pattern server-renders. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
      />

      <FAQAccordion
        title="Frequently Asked"
        items={accordionItems}
        columns={1}
        defaultOpen={[0]}
        bordered={false}
      />
    </section>
  )
}
